import React, { useState, useEffect, useRef } from 'react';
import { Search, Grid, List as ListIcon, Users } from 'lucide-react';
import CRMContactCard from '../../molecules/CRMContactCard/view';
import CRMContactRow from '../../molecules/CRMContactRow/view';
import Empty from '../../atoms/empty/view';
import NewThird from '../newThird/view';
import './style.css';

/**
 * CRMDirectory Organism
 * Houses the search filters, tab segments (All, Clients, Providers, Inactive),
 * grid/list view toggle, and dynamically renders mapped cards/rows with infinite scroll.
 */
function CRMDirectory({
    data = [],
    total = 0,
    page = 1,
    pageSize = 10,
    filters = { search: '', type: 'all' },
    onFilterChange,
    onPageChange,
    renderActions,
    isLoading = false,
}) {
    const [viewMode, setViewMode] = useState('rows'); // 'rows' or 'grid'
    const [localSearch, setLocalSearch] = useState(filters.search);
    const searchDebounceRef = useRef(null);
    const containerRef = useRef(null);
    const totalPages = Math.ceil(total / pageSize);
    const hasMore = page < totalPages;

    // Sync filters search state locally
    useEffect(() => {
        setLocalSearch(filters.search);
    }, [filters.search]);

    // Hybrid scroll listener (Percentage-based: 80% threshold)
    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current;
            if (!container) return;

            // 1. Check inner container scroll percentage
            const containerScrollTop = container.scrollTop;
            const containerClientHeight = container.clientHeight;
            const containerScrollHeight = container.scrollHeight;
            const containerMaxScroll = containerScrollHeight - containerClientHeight;
            const isNearContainerBottom = containerMaxScroll > 0 && (containerScrollTop / containerMaxScroll) >= 0.8;

            // 2. Check main window/document scroll percentage
            const docHeight = document.documentElement.scrollHeight;
            const windowScrollTop = window.scrollY || window.pageYOffset;
            const windowHeight = window.innerHeight;
            const windowMaxScroll = docHeight - windowHeight;
            const isNearWindowBottom = windowMaxScroll > 0 && (windowScrollTop / windowMaxScroll) >= 0.8;

            if ((isNearContainerBottom || isNearWindowBottom) && hasMore && !isLoading) {
                onPageChange(page + 1);
            }
        };

        // Bind events
        window.addEventListener('scroll', handleScroll, { passive: true });
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [hasMore, isLoading, page, onPageChange]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setLocalSearch(value);

        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            onFilterChange({ ...filters, search: value });
        }, 400);
    };

    const handleTabChange = (typeVal) => {
        onFilterChange({ ...filters, type: typeVal });
    };

    return (
        <div className="crm-directory">
            {/* Control Toolbar */}
            <div className="crm-toolbar">
                {/* Search */}
                <div className="crm-toolbar-search">
                    <Search size={18} className="crm-search-icon" />
                    <input
                        type="text"
                        className="crm-search-input"
                        placeholder="Buscar por nombre, documento o correo..."
                        value={localSearch}
                        onChange={handleSearchChange}
                    />
                </div>

                {/* Quick Add Button wrapper */}
                <div className="crm-toolbar-actions">
                    <NewThird id="crm-directory-new-third" />
                </div>
            </div>

            {/* Sub-toolbar: Segment Tabs & View Toggle */}
            <div className="crm-subtoolbar">
                {/* Segment Tabs */}
                <div className="crm-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.value}
                            className={`crm-tab-btn ${filters.type === tab.value ? 'crm-tab-btn--active' : ''}`}
                            onClick={() => handleTabChange(tab.value)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* View Mode Toggle */}
                <div className="crm-view-toggle">
                    <button
                        className={`crm-toggle-btn ${viewMode === 'grid' ? 'crm-toggle-btn--active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        title="Vista en cuadrícula"
                    >
                        <Grid size={16} />
                    </button>
                    <button
                        className={`crm-toggle-btn ${viewMode === 'rows' ? 'crm-toggle-btn--active' : ''}`}
                        onClick={() => setViewMode('rows')}
                        title="Vista en lista"
                    >
                        <ListIcon size={16} />
                    </button>
                </div>
            </div>

            {/* Contacts Grid/List container */}
            <div ref={containerRef} className="crm-directory-content">
                {data.length === 0 && !isLoading ? (
                    <Empty
                        icon={<Users size={32} />}
                        title="No se encontraron terceros"
                        description="Intenta cambiar los filtros de búsqueda o agrega un nuevo tercero."
                    />
                ) : data.length === 0 && isLoading ? (
                    <div className={viewMode === 'grid' ? 'crm-grid-layout' : 'crm-list-layout'}>
                        {Array.from({ length: 6 }).map((_, index) => (
                            <div
                                key={index}
                                className={viewMode === 'grid' ? 'skeleton-card-premium' : 'skeleton-row-premium'}
                            >
                                <div className="crm-skeleton-avatar" />
                                <div className="crm-skeleton-info">
                                    <div className="crm-skeleton-line title" />
                                    <div className="crm-skeleton-line subtitle" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <div className={viewMode === 'grid' ? 'crm-grid-layout' : 'crm-list-layout'}>
                            {data.map((person) => {
                                if (viewMode === 'grid') {
                                    return (
                                        <CRMContactCard
                                            key={person.id}
                                            person={person}
                                            renderActions={renderActions}
                                        />
                                    );
                                } else {
                                    return (
                                        <CRMContactRow
                                            key={person.id}
                                            person={person}
                                            renderActions={renderActions}
                                        />
                                    );
                                }
                            })}
                        </div>

                        {/* Sentinel element to trigger loading the next page */}
                        {hasMore && (
                            <div className="crm-sentinel">
                                {isLoading ? (
                                    <div className="crm-infinite-loading">
                                        <div className="crm-spinner" />
                                        <span>Cargando más terceros...</span>
                                    </div>
                                ) : (
                                    <button
                                        className="crm-load-more-btn"
                                        onClick={() => onPageChange(page + 1)}
                                    >
                                        Cargar más
                                    </button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// Tabs definition
const tabs = [
    { label: 'Todos', value: 'all' },
    { label: 'Clientes', value: 'client' },
    { label: 'Proveedores', value: 'provider' },
    { label: 'Inactivos', value: 'inactive' },
];

export default CRMDirectory;
